// The terrain carve of the emitted bores and decks (ways.json bores[] /
// decks[], registered by ways2.js), in two forms.
//
// * EMITTED BORES (`emitted`) are CUT OUT OF THE TERRAIN MESH: their runs
//   become the lining's convex segment volumes (borecut.js boreVolumes) and
//   terrain.js clips every tile triangle of every level against them at build
//   time — the drawn terrain ends on the lining wall line, at any distance,
//   for every viewer, with no texel and no shader. They register no texel, no
//   AABB in corridorBoxes (no V_COR variant) and no window base.
//
// * DECKS (`deck`): the terrain over a deck top is DISCARDED in the terrain
//   shader where spans.bin carries the deck's OPEN members (the skin stood
//   over the top): coverage is painted from those member columns with the
//   resolver's own 2x2 bilinear kernel, so the drawn hole and the collision
//   hole are ONE set (P5: carve and collider are one function).
//
// corridorCovAt / corridorCarved answer the emitted bores from the exact
// volumes (boreCut().index) and the decks from the raster.
//
// Implementation: an RGBA16F raster in a MOVING WINDOW around the player (see
// TEX/N below) — R = corridor floor height (baked track y, offset from
// uCorB.x), G = lateral coverage (1 inside, feathered to 0 outside),
// B = discard ceiling height above the floor. A fragment is discarded when
// coverage > 0.5 and its y sits in [floor-0.1, floor+ceil]. Shared uniform
// objects (worldcut.js pattern): materials compile against them before the
// sources load; addCorridors()/updateCorridors() fill them in later.
import * as THREE from 'three';
import { boreVolumes, VolumeIndex } from './borecut.js';

const H_HALF = 4.8;       // floor height written this far out (valid linear taps)
// The ceiling is HELD at the wall height for BORE_EDGE + one texel past a
// run's own `half` (boreCeil), so the bilinear tap never drags it down inside
// the lining; BORE_SLOP (below) is the same reach.
const BORE_EDGE = 0.5;
const BORE_MARGIN = 0.03; // keep the discard just under the lining surface
// Discard headroom above an emitted hole's crown (span-driven fill only).
// The carve used to open every member column to the full class ARCH, but at
// a portal band / shallow gallery the emitted crown hugs the SKIN 1.5-2.5 m
// below the arch: the roof skin got stripped up to arch height with nothing
// sealing the void from outside — the sky slit over the mouth and the ragged
// skin-vs-arch sawtooth read as "terrain filling the tunnel" (Engadiner-
// strasse gallery, (5702,5755)). Capping the discard at crown + this margin
// keeps the real roof drawn (it IS the collision crown — P5 in both
// directions) while still clearing the walkway and the mouth face below it.
const CEL_HEAD = 0.25;
// Floor gap under which two bores sharing a texel are ONE void (carve union,
// see the span-driven fill) rather than stacked runs with rock between.
const JOIN_GAP = 0.3;

// The raster is a MOVING WINDOW centred on the player, not the world. The
// carve sources are scattered over the whole world (4.6 x 4.3 km); one world
// raster at 1 m/texel would be ~14 M texels / 106 MB. A window buys correctness
// where it is looked at and costs nothing elsewhere — the same bargain the
// terrain LOD makes — and it affords a FINER texel than the world raster ever
// could, which is what keeps the excavation edge inside the lining.
const TEX = 0.75;         // m per texel
const N = 1024;           // window is N * TEX = 768 m across
const REBUILD = 128;      // recentre after this much player movement

// How far past a run's lining half-width the carve can reach: BORE_EDGE plus
// one bilinear texel. insideBoreVolume reaches this far past the lining.
const BORE_SLOP = BORE_EDGE + TEX;

const _dummy = new THREE.DataTexture(new Uint16Array([0, 0, 0, 0]), 1, 1,
  THREE.RGBAFormat, THREE.HalfFloatType);
_dummy.needsUpdate = true;
const _T = { value: _dummy };
const _A = { value: new THREE.Vector4(0, 0, 1, 1) };   // minX, minZ, 1/spanX, 1/spanZ
const _B = { value: new THREE.Vector2(0, 0) };         // base, on

const _runs = new Map();  // source key -> runs
const _cutVols = new Map();  // source key -> the emitted bores' cut volumes
let _cut = null;          // { gen, vols, index } over every key, or null
// The emitted bores' CUT volumes for terrain.js (null before ways.json
// lands): gen bumps on every registration, index = borecut VolumeIndex.
export function boreCut() { return _cut; }
let _boxes = null;        // flattened run AABBs (terrain variant test), lazy
let _gen = 0;             // bumped per registration
let _win = null;          // { cx, cz } current window centre
let _q = null;            // JS-side coverage query state
let _buf = null;          // reused scratch (cov/flr/cel/hw/inl)
let _data = null;         // reused texel buffer
let _rows = null;         // per-row texel spans written by the last rebuild
// The span resolver (game/spans.js), once main.js has loaded spans.bin. The
// deck carve is painted from the spans' own member columns — the drawn hole
// and the collision hole are ONE set (P5). Before spans land nothing is
// carved.
let _spans = null;
export function setSpans(sp) {
  if (!sp) return;
  _spans = sp;
  _win = null;              // repaint the window from the span columns
}

export function corridorUniforms(shader) {
  shader.uniforms.uCorTex = _T;
  shader.uniforms.uCorA = _A;
  shader.uniforms.uCorB = _B;
}

export const COR_DECL_GLSL = 'uniform sampler2D uCorTex;\n'
  + 'uniform vec4 uCorA;\nuniform vec2 uCorB;\n';

export function corGlsl(wp) {
  return `if (uCorB.y > 0.5) {
  vec2 cuv = (${wp}.xz - uCorA.xy) * uCorA.zw;
  if (cuv.x > 0.0 && cuv.x < 1.0 && cuv.y > 0.0 && cuv.y < 1.0) {
    vec3 cf = texture2D(uCorTex, cuv).rgb;
    float cfl = uCorB.x + cf.r;
    if (cf.g > 0.5 && ${wp}.y > cfl - 0.1 && ${wp}.y < cfl + cf.b) discard;
  }
}
`;
}

// lining half-height at lateral offset `lat` for a run's cross-section
// `prof`, outermost -> centre ([[half,wall],...,[0,crown]]).
function boreCeil(lat, prof) {
  const a = Math.abs(lat);
  // Beyond the lining the ceiling is HELD at the wall height for one feather +
  // one texel before it drops away. The raster is sampled bilinearly, so a
  // texel that reads 0 just outside the wall drags the interpolated ceiling
  // DOWN inside the tube and leaves an un-carved wedge along the wall foot —
  // most of the rock you could see in a bore. Coverage (which crosses 0.5 on
  // the lining) is what bounds the carve laterally, not this.
  if (a >= prof[0][0]) return a < prof[0][0] + BORE_EDGE + TEX ? prof[0][1] : 0;
  for (let i = 1; i < prof.length; i++) {
    const [l0, u0] = prof[i - 1], [l1, u1] = prof[i];
    if (a >= l1) return u1 + (u0 - u1) * (a - l1) / (l0 - l1 || 1);
  }
  return prof[prof.length - 1][1];
}

// Register a carve source. runs: [{ pts: [[x,y,z],...], bore, prof, deck?,
// emitted?, top?, cut? }] (deck: an emitted deck piece, carved from its spans
// members; emitted: an emitted bore, cut out of the terrain mesh — top / cut:
// ways.json bores[].section.top and bores[].cut, read by borecut.js
// boreStations; prof: the run's half-profile, outermost first). Sources
// accumulate by key and the window is rebuilt from all of them. An EMITTED
// bore keeps its place in _runs for the static insideBoreVolume query,
// flagged `cutOut` (NOT `cut`: that is the ways.json bores[].cut record
// borecut reads), and never reaches the raster.
export function addCorridors(key, runs) {
  const all = (runs || []).filter((r) => r && r.pts && r.pts.length >= 2);
  // a key registers once per world; registering it again REPLACES its runs
  // (an empty list drops them)
  if (!all.length && !_runs.has(key)) return;
  for (const r of all) {
    if (!Array.isArray(r.prof) || !r.prof.length) throw new Error(`corridor ${key}: a run without its profile (prof)`);
  }
  const cutRuns = all.filter((r) => r.bore && r.emitted && !r.deck);
  if (cutRuns.length || _cutVols.has(key)) {
    const vols = [];
    cutRuns.forEach((r, k) => { r.cutOut = true; vols.push(...boreVolumes(r, k)); });
    if (vols.length) _cutVols.set(key, vols); else _cutVols.delete(key);
    const every = [];
    for (const v of _cutVols.values()) every.push(...v);
    _cut = { gen: (_cut ? _cut.gen : 0) + 1, vols: every, index: new VolumeIndex(every) };
  }
  for (const r of all) {
    let x0 = 1e9, x1 = -1e9, z0 = 1e9, z1 = -1e9, y0 = 1e9, y1 = -1e9;
    for (const p of r.pts) {
      x0 = Math.min(x0, p[0]); x1 = Math.max(x1, p[0]);
      z0 = Math.min(z0, p[2]); z1 = Math.max(z1, p[2]);
      y0 = Math.min(y0, p[1]); y1 = Math.max(y1, p[1]);
    }
    r._bb = [x0 - H_HALF, x1 + H_HALF, z0 - H_HALF, z1 + H_HALF];
    // vertical reach of the carve: the shader discards from floor-0.1 up to
    // floor+ceiling, the profile's centre height
    const ceil = r.prof[r.prof.length - 1][1];
    r._aabb = [r._bb[0], r._bb[1], r._bb[2], r._bb[3], y0 - 0.5, y1 + ceil + 0.5];
  }
  _runs.set(key, all);
  _boxes = null;                    // rebuilt lazily from _runs
  _gen++;
  _win = null;                      // force a rebuild with the new source
}

// World AABBs of every registered carve run, for terrain's static
// shader-variant test — a tile that meets none compiles no corridor discard.
export function corridorBoxes() {
  if (!_boxes) {
    _boxes = [];
    for (const runs of _runs.values()) for (const r of runs) if (!r.cutOut) _boxes.push(r._aabb);
  }
  return _boxes;
}
// bumped whenever a carve source registers, so terrain can re-classify tiles
export function corridorGen() { return _gen; }

// Static point-in-carve query for vegetation placement (the grass exclude;
// baked trees / shrubs: bake.py VegCover, t39): true when a world position sits INSIDE a registered bore's
// carve volume — under the lining arch at its lateral offset, at/above the
// chord bed. Unlike the moving-window query this is exact map-wide, which
// placement needs (instances are filtered once, wherever they are). The
// driving case: nDSM trees planted on a portal-face DTM smear stood INSIDE
// a rail tunnel mouth at (6973,2367) — their base y is under the arch, while
// legitimate roof trees over the same chord sit ABOVE it and are kept.
//
// A TRUNK UNDER A BORE FLOOR STANDS THROUGH IT (S6.d B4b-2): inside an
// EMITTED bore's lining footprint (ways.json bores[], registered by ways2.js
// as `emitted`; lateral <= the section's half width) a base UNDER the floor
// is dropped down to floor - sinkM: every measured tree is at least
// meta.trees.min_h_m tall, so a trunk based less than that under the floor
// reaches up through it into the bore (bore 184, Via Pradè: the rail
// bridge's nDSM 'conifer' based 1.0 m under the floor, kept by the old
// floor - 0.5 m bound). sinkM: the caller's meta.trees.min_h_m; this module
// has no meta, so when the caller passes none the 3 m FALLBACK applies
// (= min_h_m of every measured bake to date). Decks keep floor - 0.5 m (a
// tree under a viaduct is real).
const TRUNK_SINK_FALLBACK = 3.0;
// The bore runs by BORE_CELL_M grid cell of their _bb (quest-move 2026-11-02:
// the grass exclude asks per cold cell, ~830 cells a re-scatter, and the
// linear walk over every registered run was the re-scatter's biggest term).
// Rebuilt lazily when a source registers (_gen); a query reads the one cell
// it falls in, which holds every run whose _bb can contain it: the answer
// is the linear walk's, exactly.
const BORE_CELL_M = 256;
let _boreIdx = null;              // { gen, cells: Map(cellKey -> runs[]) }
const boreKey = (cx, cz) => (cx + 32768) * 65536 + (cz + 32768);
function boreIndex() {
  if (_boreIdx && _boreIdx.gen === _gen) return _boreIdx.cells;
  const cells = new Map();
  for (const runs of _runs.values()) {
    for (const r of runs) {
      if (!r.bore || !r.prof) continue;
      const bb = r._bb;
      const cx0 = Math.floor(bb[0] / BORE_CELL_M), cx1 = Math.floor(bb[1] / BORE_CELL_M);
      const cz0 = Math.floor(bb[2] / BORE_CELL_M), cz1 = Math.floor(bb[3] / BORE_CELL_M);
      for (let cx = cx0; cx <= cx1; cx++) {
        for (let cz = cz0; cz <= cz1; cz++) {
          const k = boreKey(cx, cz);
          const l = cells.get(k);
          if (l) l.push(r); else cells.set(k, [r]);
        }
      }
    }
  }
  _boreIdx = { gen: _gen, cells };
  return cells;
}
export function insideBoreVolume(x, y, z, sinkM) {
  const sink = sinkM > 0 ? sinkM : TRUNK_SINK_FALLBACK;
  if (!_runs.size) return false;
  const runs = boreIndex().get(boreKey(Math.floor(x / BORE_CELL_M), Math.floor(z / BORE_CELL_M)));
  if (runs) {
    for (const r of runs) {
      const bb = r._bb;
      if (x < bb[0] || x > bb[1] || z < bb[2] || z > bb[3]) continue;
      const aabb = r._aabb;
      const lo = r.emitted && !r.deck ? Math.max(0.5, sink) : 0.5;
      if (y < aabb[4] + 0.5 - lo || y > aabb[5]) continue;
      const reach = r.prof[0][0] + BORE_SLOP;
      const pts = r.pts;
      for (let i = 0; i < pts.length - 1; i++) {
        const a = pts[i], b = pts[i + 1];
        const dx = b[0] - a[0], dz = b[2] - a[2];
        const L2 = dx * dx + dz * dz;
        if (L2 < 1e-6) continue;
        let t = ((x - a[0]) * dx + (z - a[2]) * dz) / L2;
        t = t < 0 ? 0 : t > 1 ? 1 : t;
        const lat = Math.hypot(x - (a[0] + dx * t), z - (a[2] + dz * t));
        if (lat > reach) continue;
        const cy = a[1] + (b[1] - a[1]) * t;
        const under = lat <= r.prof[0][0] ? lo : 0.5;
        if (y > cy - under && y < cy + boreCeil(lat, r.prof)) return true;
      }
    }
  }
  return false;
}

// Recentre the window on the player when they have walked far enough. Cheap:
// only runs whose bbox meets the window are rasterised, and the texel buffer
// is reused.
export function updateCorridors(pos) {
  if (!_runs.size || !pos) return;
  if (_win && Math.abs(pos.x - _win.cx) < REBUILD && Math.abs(pos.z - _win.cz) < REBUILD) return;
  const span = N * TEX;
  const minX = pos.x - span / 2, minZ = pos.z - span / 2;
  if (!_buf) {
    _buf = { cov: new Float32Array(N * N), flr: new Float32Array(N * N),
      cel: new Float32Array(N * N), hw: new Float32Array(N * N),
      inl: new Uint8Array(N * N) };
    _data = new Uint16Array(N * N * 4);
  }
  const { cov, flr, cel, hw, inl } = _buf;
  // Only a sliver of the window is ever corridor, so clear and convert just the
  // touched texels. Converting all 1 M of them to half-float cost 32 ms — two
  // dropped frames at 72 Hz, every REBUILD metres.
  //
  // Those texels are tracked as a SPAN PER ROW, not as one bounding rect. A
  // corridor is a thin diagonal ribbon: one long run crosses the whole
  // window corner to corner, so its bounding rect is essentially the entire
  // 1024x1024 window even though each row of it holds ~13 live texels. The
  // rect makes the "touched only" optimisation collapse back to the
  // full-window cost precisely for the runs that matter. Per-row spans are
  // ~80x tighter.
  if (_rows) {
    for (let iz = _rows.z0; iz <= _rows.z1; iz++) {
      const lo = _rows.min[iz];
      if (lo > _rows.max[iz]) continue;
      const a = iz * N + lo, b = iz * N + _rows.max[iz] + 1;
      cov.fill(0, a, b); flr.fill(0, a, b); cel.fill(0, a, b); hw.fill(0, a, b);
      inl.fill(0, a, b);
      _data.fill(0, a * 4, b * 4);
    }
  }
  if (!_rows) _rows = { min: new Int32Array(N), max: new Int32Array(N), z0: 0, z1: -1 };
  _rows.min.fill(N); _rows.max.fill(-1);
  let dz0 = N, dz1 = -1;
  const rmin = _rows.min, rmax = _rows.max;
  let base = null;
  for (const runs of _runs.values()) {
    for (const r of runs) {
      const bb = r._bb;
      if (r.cutOut || bb[1] < minX || bb[0] > minX + span || bb[3] < minZ || bb[2] > minZ + span) continue;
      for (const p of r.pts) base = base === null ? p[1] : Math.min(base, p[1]);
    }
  }
  if (base === null) {
    // nothing to carve here; the clear above already zeroed last round's spans
    _win = { cx: pos.x, cz: pos.z }; _B.value.set(0, 0); _q = null;
    _rows.z0 = 0; _rows.z1 = -1;
    return;
  }
  base -= 2;
  for (const runs of _runs.values()) {
    for (const r of runs) {
      const bb = r._bb;
      if (r.cutOut || bb[1] < minX || bb[0] > minX + span || bb[3] < minZ || bb[2] > minZ + span) continue;
      // carved from its SPAN COLUMNS or not at all: without spans there is
      // no resolver hole behind the pixels, and an open-looking hole a body
      // bounces off is worse than visible ground
      if (!_spans) continue;
      const pts = r.pts;
      const prof = r.prof;
      for (let i = 0; i < pts.length - 1; i++) {
        const a = pts[i], b = pts[i + 1];
        const dx = b[0] - a[0], dz = b[2] - a[2];
        const L2 = dx * dx + dz * dz;
        if (L2 < 1e-6) continue;
        const x0 = Math.max(0, Math.floor((Math.min(a[0], b[0]) - H_HALF - minX) / TEX));
        const x1 = Math.min(N - 1, Math.ceil((Math.max(a[0], b[0]) + H_HALF - minX) / TEX));
        const z0 = Math.max(0, Math.floor((Math.min(a[2], b[2]) - H_HALF - minZ) / TEX));
        const z1 = Math.min(N - 1, Math.ceil((Math.max(a[2], b[2]) + H_HALF - minZ) / TEX));
        if (x1 < x0 || z1 < z0) continue;
        if (z0 < dz0) dz0 = z0;
        if (z1 > dz1) dz1 = z1;
        for (let iz = z0; iz <= z1; iz++) {
          for (let ix = x0; ix <= x1; ix++) {
            const px = minX + ix * TEX, pz = minZ + iz * TEX;
            let t = ((px - a[0]) * dx + (pz - a[2]) * dz) / L2;
            t = t < 0 ? 0 : t > 1 ? 1 : t;
            const lat = Math.hypot(px - (a[0] + dx * t), pz - (a[2] + dz * t));
            if (lat > H_HALF) continue;
            // past the lateral gate this texel can carry data, so it must be in
            // both the convert span now and the clear span next rebuild
            if (ix < rmin[iz]) rmin[iz] = ix;
            if (ix > rmax[iz]) rmax[iz] = ix;
            const k = iz * N + ix;
            const chordY = a[1] + (b[1] - a[1]) * t;
            // span-driven fill: coverage = 2x2 bilinear column
            // membership (the resolver's own kernel — the 0.5 crossing
            // lands on the emit's exact footprint boundary), floor = the
            // member hole nearest this run's chord, ceiling = the lining
            // arch capped at the hole's own crown + CEL_HEAD. The eye and
            // the resolver see the SAME roof: a squashed portal/gallery
            // hole keeps its real skin drawn overhead instead of having it
            // stripped to arch height (the unsealed void that read as
            // spikes + sky over a gallery mouth — see CEL_HEAD).
            const fx = px / _spans.res - 0.5, fz = pz / _spans.res - 0.5;
            const gx0 = Math.floor(fx), gz0 = Math.floor(fz);
            const wx = fx - gx0, wz = fz - gz0;
            let cw = 0, bd = 1e9, bf = 0, bc = null;
            for (let j = 0; j < 4; j++) {
              const cgx = gx0 + (j & 1), cgz = gz0 + (j >> 1);
              const hs = _spans.colAt(cgx, cgz);
              if (!hs) continue;
              let dn = 1e9, fn = 0, cn = null;
              for (const h of hs) {
                const d = Math.abs(h.floor - chordY);
                if (d < dn) { dn = d; fn = h.floor; cn = h.crown; }
              }
              if (dn > 6) continue;       // another run's stacked hole
              cw += (j & 1 ? wx : 1 - wx) * (j >> 1 ? wz : 1 - wz);
              if (dn < bd) { bd = dn; bf = fn; bc = cn; }
            }
            if (bd > 6) continue;
            if (cw > cov[k]) cov[k] = cw;
            const w = H_HALF - lat;
            // ceiling = the lining arch, CAPPED at the emitted hole's
            // crown + CEL_HEAD (see there) — an open-sky column (crown
            // null, a mouth pit) has no roof to preserve and opens to
            // the arch
            const arch = Math.max(0, boreCeil(lat, prof) - BORE_MARGIN);
            const ce = bc === null ? arch
              : Math.min(arch, Math.max(0, bc - bf + CEL_HEAD));
            // Carve UNION where two linings share a texel at one floor
            // (|floor gap| <= JOIN_GAP, both texels inside their OWN
            // lining — so the discarded void is sealed by whichever arch
            // is higher): the ceiling is the higher of the two. Nearest-
            // run-only left the lower arch's cap in the other tube at a
            // T-junction (a stair sibling entering a subway), and the
            // skin between the two arches read as rock inside the tube.
            // Stacked runs (rock between) keep the nearest-run rule.
            const inside = lat <= prof[0][0];
            if (w > hw[k]) {
              const join = inl[k] && inside
                && Math.abs(flr[k] - (bf - base)) <= JOIN_GAP;
              const top = flr[k] + cel[k];
              hw[k] = w;
              flr[k] = bf - base;
              cel[k] = join ? Math.max(ce, top - flr[k]) : ce;
              inl[k] = inside ? 1 : 0;
            } else if (inside && inl[k]
              && Math.abs(flr[k] - (bf - base)) <= JOIN_GAP) {
              cel[k] = Math.max(cel[k], bf - base + ce - flr[k]);
            }
          }
        }
      }
    }
  }
  for (let iz = dz0; iz <= dz1; iz++) {
    const hi = rmax[iz];
    for (let ix = rmin[iz]; ix <= hi; ix++) {
      const k = iz * N + ix;
      _data[k * 4] = THREE.DataUtils.toHalfFloat(flr[k]);
      _data[k * 4 + 1] = THREE.DataUtils.toHalfFloat(cov[k]);
      _data[k * 4 + 2] = THREE.DataUtils.toHalfFloat(cel[k]);
    }
  }
  _rows.z0 = dz0; _rows.z1 = dz1;
  if (_T.value === _dummy) {
    const tex = new THREE.DataTexture(_data, N, N, THREE.RGBAFormat, THREE.HalfFloatType);
    tex.magFilter = tex.minFilter = THREE.LinearFilter;
    _T.value = tex;
  }
  _T.value.needsUpdate = true;
  _A.value.set(minX, minZ, 1 / (N * TEX), 1 / (N * TEX));
  _B.value.set(base, 1);
  _win = { cx: pos.x, cz: pos.z };
  _q = { minX, minZ, cov, flr, cel, base };
}

function _tex(x, z) {
  if (!_q) return -1;
  const ix = Math.round((x - _q.minX) / TEX), iz = Math.round((z - _q.minZ) / TEX);
  if (ix < 0 || iz < 0 || ix >= N || iz >= N) return -1;
  return iz * N + ix;
}

// JS-side coverage query (grass suppression, rider re-clamp skip): 0 outside,
// 1 on the track — true for both cut and bore runs
export function corridorCovAt(x, z) {
  if (_cut && _cut.index.inPlan(x, z)) return 1;
  const k = _tex(x, z);
  return k < 0 ? 0 : _q.cov[k];
}

// Is the terrain at (x, y, z) CARVED AWAY? Exactly the shader's own test, so
// scatter can be suppressed precisely where the ground it stands on was
// discarded — grass tufts and rocks were left hanging in mid-air inside the
// tunnels, because the old "near the track floor" rule kept everything more
// than 3 m above the bed and a bore's roof is 3.8 m up.
export function corridorCarved(x, y, z) {
  // an emitted bore: exactly the volume terrain.js cut the mesh with
  if (_cut && _cut.index.inside(x, y, z)) return true;
  const k = _tex(x, z);
  if (k < 0 || _q.cov[k] <= 0.5) return false;
  const floor = _q.base + _q.flr[k];
  return y > floor - 0.1 && y < floor + _q.cel[k];
}
